import {useQuery} from "@tanstack/react-query";
import {ENGLISH_LANG_CODE, IShow} from "@evennt-tech/api-v2-core";
import {apiV2} from "api/firebase";
import {RequestKey} from "api/request-key";
import {useLocalisedLanguageCode} from "hooks/translation";
import useCurrentShow from "./useCurrentShow";

type UseCurrentLocalisedShowResult = [IShow | undefined, boolean];

type Options = {
  showId?: string;
  enabled?: boolean;
};

const useCurrentLocalisedShow = (
  options: Options = {}
): UseCurrentLocalisedShowResult => {
  const {showId: providedShowId, enabled = true} = options;
  const languageCode = useLocalisedLanguageCode(true);
  const isEnglish = languageCode === ENGLISH_LANG_CODE;

  const [sourceShow, isSourceLoading] = useCurrentShow({
    showId: providedShowId,
    enabled,
  });
  const showId = providedShowId ?? sourceShow?.id;

  const {data, isLoading} = useQuery({
    queryKey: [RequestKey.GetLocalisedShowApiV2, showId, languageCode],
    queryFn: () => apiV2.show.getOne({showId: showId as string, languageCode}),
    enabled: enabled && !isEnglish && !!showId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  if (isEnglish) {
    return [sourceShow, isSourceLoading];
  }

  return [data ?? sourceShow, isLoading && !data];
};

export default useCurrentLocalisedShow;
